/**
 * A festival you are going to, on the phone before you get there (operator, 2026-09-23: "people will already have
 * these downloaded ... if they don't have service and they want to check who's coming up next").
 *
 * The offline set is the published map, the lineup (set times) and your picks. It downloads by itself from three
 * days before the first day, stays a week (GC.festival, and the persisted cache's 7-day maxAge), and is deleted three
 * days after the festival is over. The Maps list shows "Offline" per festival and offers a Download for anything not yet saved.
 */
import { supabase } from '../supabaseClient';
import { fetchFestivalMap } from '../api/festivalMap';
import { fetchEventLineup, fetchMyArtistVotes } from '../api/artistVotes';
import { queryKeys } from './query/queryKeys';
import { STALE, GC } from './query/staleTimes';
import { getEventEndsAt } from '../utils/eventCutoff';
import { warmImageCache } from './images/imageCache';
import { whenQuiet } from './quietMoment';
import { mapStore } from './map/mapStore';
import { mapTiming, mapNow as nowMs } from './map/mapTiming';
import { appLog } from './appLog';
import { FESTIVAL_MAPS_ENABLED } from '../config/festivalMaps';
import { seedGeoShapes } from './map3d/geoCache';

const DAY = 24 * 60 * 60 * 1000;

export const DOWNLOAD_LEAD_MS = 3 * DAY; // auto-download from here before the first day
export const PURGE_AFTER_MS = 3 * DAY; // and its download is deleted this long after it is over

function startMs(event) {
  const raw = event?.event_datetime ?? event?.event_date ?? null;
  const t = raw ? new Date(raw).getTime() : NaN;
  return Number.isFinite(t) ? t : null;
}

/** When the festival is over: events.ends_at, 6 AM after its last day (utils/eventCutoff). */
function endMs(event) {
  return getEventEndsAt(event)?.getTime() ?? null;
}

/** From the morning of the first day until the festival is over. */
export function isFestivalOnNow(event, now = Date.now()) {
  const t = startMs(event);
  const end = endMs(event);
  if (t == null || end == null) return false;
  const morning = new Date(t);
  morning.setHours(0, 0, 0, 0);
  return now >= morning.getTime() && now < end;
}

/** Not over yet (Maps lists a festival until then). */
export function isFestivalNotOver(event, now = Date.now()) {
  const end = endMs(event);
  return end == null || now < end;
}

export function isInDownloadWindow(event, now = Date.now()) {
  const t = startMs(event);
  const end = endMs(event);
  return t != null && end != null && now >= t - DOWNLOAD_LEAD_MS && now < end;
}

export function isPastPurge(event, now = Date.now()) {
  const end = endMs(event);
  return end != null && now > end + PURGE_AFTER_MS;
}

/** Saved on this phone: the map (or a known "no map") is in the map store and the lineup is in the cache. */
export function isFestivalOffline(queryClient, eventId) {
  return (
    mapStore.has(eventId) &&
    queryClient.getQueryState(queryKeys.events.lineup(eventId))?.data !== undefined
  );
}

/**
 * The map an event shows, phone first (useFestivalMap's queryFn): the stored copy when there is one - offline, on a
 * cold start, at the gates - else the network, which is then stored. Whether a stored copy is current is
 * refreshMapIfChanged's job, a 0.2 s version check, not a re-download.
 */
export async function loadFestivalMap(eventId) {
  const t0 = nowMs();
  const stored = await mapStore.read(eventId);
  if (stored !== undefined) {
    mapTiming('map.load', { eventId, source: 'store', ms: nowMs() - t0 });
    void seedGeoShapes(mapStore.version(eventId)); // a map code change since: its shipped shapes, if rendered
    return stored;
  }
  // Not seen yet, but maybe another show at the same venue was: the version (0.2 s) names the edition, and a copy already
  // on the phone answers with no download (Sam, 2026-10-22).
  const version = await fetchMapVersion(eventId).catch(() => undefined);
  if (version && (await mapStore.adopt(eventId, version))) {
    const shared = await mapStore.read(eventId);
    if (shared !== undefined) {
      mapTiming('map.load', { eventId, source: 'venue', ms: nowMs() - t0 });
      return shared;
    }
  }
  const payload = await fetchAndStoreMap(eventId, version !== undefined ? { version } : undefined)
    .fetched;
  mapTiming('map.load', { eventId, source: 'network', ms: nowMs() - t0, hasMap: Boolean(payload) });
  return payload;
}

/**
 * The network copy, stored with its version stamp. One download per event at a time: the event page's query, the
 * version check and the festival-day prefetch all asked at once on a first open, and each pulled the whole payload
 * (2026-10-13 RSVP-to-map audit). `fetched` resolves as soon as the payload is here, so the map shows without
 * waiting for the phone's store (whose write also prunes, reading every stored map); `stored` once it is saved.
 */
function fetchAndStoreMap(eventId, { pin, version: knownVersion } = {}) {
  let job = inflightMaps.get(eventId);
  if (!job) {
    const t0 = nowMs();
    const fetched = Promise.all([
      fetchFestivalMap(eventId),
      knownVersion !== undefined ? knownVersion : fetchMapVersion(eventId).catch(() => null),
    ]);
    const stored = fetched
      .then(([payload, version]) => {
        mapTiming('map.download', { eventId, ms: nowMs() - t0, hasMap: Boolean(payload), version });
        // Saving copies the whole map on the main thread; it is already in hand and on screen, so the copy waits for a
        // quiet moment (at most 20 s) instead of landing on the tap that leaves the page (2026-10-30)
        // An explicit download for offline (pin) saves at once: it is what the Download button is waiting on.
        const quiet = pin
          ? Promise.resolve()
          : new Promise((resolve) => whenQuiet(resolve, { quietMs: 1500, maxWaitMs: 20000 }));
        return quiet
          .then(() => mapStore.write(eventId, payload, { version, pin }))
          .then((r) => {
            void seedGeoShapes(version);
            return r;
          });
      })
      .catch((err) => {
        console.warn('[map-store] download or write failed:', err);
        // A write that keeps failing leaves the old copy answering every open: say so in app_logs (Sam, 2026-10-19).
        appLog('warn', 'map.store_write_failed', {
          eventId,
          message: String(err?.message ?? err).slice(0, 300),
        });
      })
      .finally(() => {
        if (inflightMaps.get(eventId) === job) inflightMaps.delete(eventId);
      });
    job = { fetched: fetched.then(([payload]) => payload), stored };
    inflightMaps.set(eventId, job);
  } else if (pin) {
    job.stored.then(() => mapStore.pin(eventId)).catch(() => {});
  }
  return job;
}
const inflightMaps = new Map(); // eventId -> { fetched, stored }

/**
 * Re-download the event's map only when its version stamp changed (or it is not stored), into the store and on screen.
 * At most one check per event per 2 min (throttle: false skips that); force re-downloads whatever the stamp says (an
 * editor save).
 */
export async function refreshMapIfChanged(
  queryClient,
  eventId,
  { force = false, throttle = true } = {},
) {
  if (!eventId) return;
  const now = Date.now();
  if (!force && throttle && now - (lastMapCheckAt.get(eventId) ?? 0) < CHECK_EVERY_MS) return;
  lastMapCheckAt.set(eventId, now);
  if (!force && inflightMaps.has(eventId)) return; // a download is already under way: it is the latest
  await mapStore.ready();
  let remote;
  try {
    remote = await fetchMapVersion(eventId);
  } catch {
    return; // no service: keep what is on the phone
  }
  if (!force && mapStore.has(eventId) && mapStore.version(eventId) === remote) {
    void seedGeoShapes(remote);
    return;
  }
  // A show not seen yet at a venue whose map is on the phone: linked to that copy, nothing to download (the page's own
  // load does the same; whichever runs first links it).
  // So is a show whose own copy is older while another show at the venue already brought the new one down: the second Bowl
  // show downloaded the same new map again, and swapped it in (Sam's phone, 2026-10-23).
  if (!force && remote && (await mapStore.adopt(eventId, remote))) {
    const had = mapStore.has(eventId);
    const payload = had ? await mapStore.read(eventId) : undefined;
    if (payload !== undefined) {
      if (mapOnScreen === eventId) pendingMaps.set(eventId, { queryClient, payload });
      else queryClient.setQueryData(queryKeys.festivalMap.published(eventId), payload);
    }
    return;
  }
  if (!force && inflightMaps.has(eventId)) return; // one started while the version was asked for
  try {
    const job = fetchAndStoreMap(eventId, { version: remote });
    const payload = await job.fetched;
    // The page's own query may still be reading the OLD copy off the device; landing after this, it put the old map
    // back on screen, open after open (Sam's localhost Bowl, 2026-10-19). Cancel it so the newer copy is what stays.
    await queryClient.cancelQueries({
      queryKey: queryKeys.festivalMap.published(eventId),
      exact: true,
    });
    // Never under you: the map on screen keeps its scene, and the new copy takes over when you leave it. Swapped in while
    // open, the whole scene rebuilt next to the old one - on the Bowl, a 36 s freeze and then a killed web view (Sam's
    // phone, 2026-10-23).
    if (mapOnScreen === eventId) pendingMaps.set(eventId, { queryClient, payload });
    else queryClient.setQueryData(queryKeys.festivalMap.published(eventId), payload);
    await job.stored;
  } catch {
    /* keep the stored copy */
  }
}
const lastMapCheckAt = new Map();

/** The map on screen (MapsPage), whose newer copy waits until you leave it. */
let mapOnScreen = null;
const pendingMaps = new Map(); // eventId -> { queryClient, payload }
export function setMapOnScreen(eventId) {
  const was = mapOnScreen;
  mapOnScreen = eventId || null;
  if (was && was !== mapOnScreen && pendingMaps.has(was)) {
    const { queryClient, payload } = pendingMaps.get(was);
    pendingMaps.delete(was);
    queryClient.setQueryData(queryKeys.festivalMap.published(was), payload);
  }
}

/**
 * Start fetching an event's map the moment you are going (an RSVP, a group join), so it is on the phone before you
 * open Maps. The same query and queryFn as useFestivalMap; a stored map answers from the phone, an event with no map
 * costs one small request and stores its "no map".
 */
export function warmEventMap(queryClient, eventId) {
  if (!queryClient || !eventId || !FESTIVAL_MAPS_ENABLED) return;
  void queryClient.prefetchQuery({
    queryKey: queryKeys.festivalMap.published(eventId),
    queryFn: () => loadFestivalMap(eventId),
    networkMode: 'offlineFirst',
    staleTime: Infinity, // as useFestivalMap: a map already in the cache is never re-read (a re-read rebuilds the scene)
    structuralSharing: false,
    gcTime: GC.festival,
  });
}

/** Fetch the offline set now. Resolves true when all of it landed. */
export async function downloadFestival(queryClient, eventId, userId) {
  const jobs = [
    (async () => {
      const job = fetchAndStoreMap(eventId, { pin: true });
      const payload = await job.fetched;
      queryClient.setQueryData(queryKeys.festivalMap.published(eventId), payload);
      await job.stored; // "Offline" means saved on the phone, not only fetched
      return payload;
    })(),
    queryClient.fetchQuery({
      queryKey: queryKeys.events.lineup(eventId),
      queryFn: () => fetchEventLineup(eventId),
      staleTime: STALE.semiStatic,
      gcTime: GC.festival,
    }),
  ];
  if (userId) {
    jobs.push(
      queryClient.fetchQuery({
        queryKey: queryKeys.events.myArtistVotes(eventId, userId),
        queryFn: () => fetchMyArtistVotes(eventId, userId),
        staleTime: STALE.user,
        gcTime: GC.festival,
      }),
    );
  }
  const results = await Promise.allSettled(jobs);
  const ok = results.every((r) => r.status === 'fulfilled');
  if (ok) {
    // The pictures too: the event's art and the lineup's faces, into the image store (lib/images/imageCache.js).
    try {
      const lineup = results[1].status === 'fulfilled' ? results[1].value : [];
      const { data: ev } = await supabase
        .from('events')
        .select('card_image_url, hero_image_url')
        .eq('event_id', eventId)
        .maybeSingle();
      warmImageCache([
        ev?.card_image_url,
        ev?.hero_image_url,
        ...(Array.isArray(lineup)
          ? lineup.map((r) => r?.artist?.spotify_image_url ?? r?.spotify_image_url)
          : []),
      ]);
    } catch {
      /* art is a nice-to-have */
    }
  }
  return ok;
}

const CHECK_EVERY_MS = 2 * 60 * 1000;
const lastCheckAt = new Map();

async function fetchMapVersion(eventId) {
  const { data, error } = await supabase.rpc('get_festival_map_version', { p_event_id: eventId });
  if (error) throw error;
  return data ?? null;
}

/**
 * With signal, make sure the phone has the latest (operator, 2026-09-23: maps land a day or two before and set times
 * change on the day). The map is re-downloaded only when its version stamp changed (get_festival_map_version,
 * migration 129); the lineup and your picks are small and simply refetched. With no signal every request fails fast
 * and the downloaded copy stays in use. At most one check per festival per 2 min.
 */
export async function refreshFestivalIfChanged(
  queryClient,
  eventId,
  userId,
  { force = false } = {},
) {
  if (!eventId) return;
  const now = Date.now();
  if (!force && now - (lastCheckAt.get(eventId) ?? 0) < CHECK_EVERY_MS) return;
  lastCheckAt.set(eventId, now);
  const jobs = [
    refreshMapIfChanged(queryClient, eventId, { throttle: false }),
    queryClient.fetchQuery({
      queryKey: queryKeys.events.lineup(eventId),
      queryFn: () => fetchEventLineup(eventId),
      staleTime: 0,
      gcTime: GC.festival,
    }),
  ];
  if (userId) {
    jobs.push(
      queryClient.fetchQuery({
        queryKey: queryKeys.events.myArtistVotes(eventId, userId),
        queryFn: () => fetchMyArtistVotes(eventId, userId),
        staleTime: 0,
        gcTime: GC.festival,
      }),
    );
  }
  await Promise.allSettled(jobs);
}

/** Delete a festival's offline set (it is over). */
export function purgeFestival(queryClient, eventId) {
  void mapStore.remove(eventId).catch(() => {});
  queryClient.removeQueries({ queryKey: queryKeys.festivalMap.published(eventId) });
  queryClient.removeQueries({ queryKey: queryKeys.events.lineup(eventId) });
}

/**
 * Maps downloaded before the map store lived in the persisted query cache. Copy them in once, after the cache
 * restores, so an update never costs anyone a download (2026-09-25: an overnight update left people at the gates
 * with nothing). A map whose lineup was saved with it was part of an offline set, so it is pinned.
 */
export async function importPersistedMaps(queryClient) {
  const queries = queryClient.getQueryCache().findAll({ queryKey: ['festival-map'] });
  for (const q of queries) {
    const [, eventId, extra] = q.queryKey;
    if (typeof eventId !== 'string' || eventId === 'mine' || eventId === 'upcoming') continue;
    if (extra === 'version') {
      queryClient.removeQueries({ queryKey: q.queryKey, exact: true });
      continue;
    }
    if (extra !== undefined || q.state.data === undefined) continue;
    await mapStore.ready();
    if (mapStore.has(eventId)) continue;
    const version =
      queryClient.getQueryData([...queryKeys.festivalMap.published(eventId), 'version']) ?? null;
    const pin = queryClient.getQueryState(queryKeys.events.lineup(eventId))?.data !== undefined;
    await mapStore.write(eventId, q.state.data, { version, pin }).catch((err) => {
      console.warn('[map-store] import failed:', err);
    });
  }
}
